import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { getWorkOrders, getExpenses, getShifts } from './utils/api';
import { saveWorkOrders, saveExpenses, saveShifts } from './utils/offlineDb';
import { LoginForm } from './components/LoginForm';
import { Layout } from './components/Layout';
import { TimeTracker } from './components/TimeTracker';
import { WorkOrdersView } from './components/workOrders/WorkOrdersView';
import { ExpensesView } from './components/expenses/ExpensesView';
import { ShiftsView } from './components/shifts/ShiftsView';
import { DashboardView } from './components/dashboard/DashboardView';
import { FormsDocsView } from './components/forms-docs/FormsDocsView';
import { InitialLoading } from './components/InitialLoading';
// import { PWAPrompt } from './components/PWAPrompt';

export default function App() {
    const { isAuthenticated, accessToken, workerId } = useAuthStore();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!accessToken || !workerId) {
            useAuthStore.getState().logout();
            setIsLoading(false);
        }
    }, [accessToken, workerId]);

    useEffect(() => {
        const loadInitialData = async () => {
            if (!isAuthenticated || !accessToken || !workerId) {
                setIsLoading(false);
                return;
            }

            try {
                // Fetch all data in parallel
                const [workOrdersResponse, expensesResponse, shiftsResponse] =
                    await Promise.all([
                        getWorkOrders(accessToken, workerId),
                        getExpenses(accessToken),
                        getShifts(accessToken),
                    ]);

                // Save all data to IndexedDB
                await Promise.all([
                    saveWorkOrders(workOrdersResponse.orders || []),
                    saveExpenses(expensesResponse.expenses || []),
                    saveShifts(shiftsResponse || []),
                ]);
            } catch (error) {
                console.error('Failed to load initial data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [isAuthenticated, accessToken, workerId]);

    if (isLoading) {
        return <InitialLoading />;
    }

    return (
        <BrowserRouter>
            <Toaster position='top-right' />
            {/* <PWAPrompt /> */}
            <Routes>
                <Route
                    path='/login'
                    element={
                        !isAuthenticated ? <LoginForm /> : <Navigate to='/' />
                    }
                />
                <Route element={<Layout />}>
                    <Route
                        path='/'
                        element={
                            isAuthenticated ? (
                                <DashboardView />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                    <Route
                        path='/time'
                        element={
                            isAuthenticated ? (
                                <TimeTracker />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                    <Route
                        path='/work-orders'
                        element={
                            isAuthenticated ? (
                                <WorkOrdersView />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                    <Route
                        path='/expenses'
                        element={
                            isAuthenticated ? (
                                <ExpensesView />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                    <Route
                        path='/shifts'
                        element={
                            isAuthenticated ? (
                                <ShiftsView />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                    <Route
                        path='/forms-docs'
                        element={
                            isAuthenticated ? (
                                <FormsDocsView />
                            ) : (
                                <Navigate to='/login' />
                            )
                        }
                    />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}
